import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight,
  TrendingUp,
  Zap,
  Wallet,
  Clock,
  ShieldCheck,
  BadgeCheck,
  Sparkles,
  CheckCircle2,
  Building2,
  Store,
  Users,
  LineChart,
  Download,
  HeadphonesIcon,
  Mail,
  Lock,
  CalendarClock,
  Layers,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import FooterSection from '../components/FooterSection';
import SEO from '../components/SEO';
import { LanguageProvider } from '../context/LanguageContext';

// ---------------------------------------------------------------------------
// NoFee Partners — premium enterprise SaaS landing page.
// Black + Gold; left-aligned typographic rhythm; restrained motion.
// ---------------------------------------------------------------------------

const StatCard = ({ icon: Icon, label, value, accent = false }) => (
  <div
    className={`relative rounded-2xl p-6 border ${
      accent
        ? 'bg-gradient-to-br from-amber-500/[0.08] to-gold-500/[0.04] border-gold-500/30'
        : 'bg-white/[0.02] border-white/[0.08]'
    }`}
  >
    <Icon className={`w-5 h-5 mb-3 ${accent ? 'text-gold-300' : 'text-white/55'}`} />
    <div className={`text-3xl font-black ${accent ? 'text-gold-200' : 'text-white'}`}>{value}</div>
    <div className="text-xs uppercase tracking-[0.2em] text-white/45 mt-2 font-semibold">
      {label}
    </div>
  </div>
);

const BenefitCard = ({ icon: Icon, title, body, kicker, footer }) => (
  <div className="group relative rounded-2xl bg-white/[0.025] border border-white/[0.06] p-7 hover:border-gold-500/30 transition-all">
    <div className="absolute top-0 right-0 w-24 h-24 bg-gold-500/[0.04] blur-3xl rounded-full pointer-events-none" />
    {kicker && (
      <p className="text-[10.5px] uppercase tracking-[0.22em] text-gold-400 font-bold mb-3">
        {kicker}
      </p>
    )}
    <div className="w-11 h-11 rounded-xl bg-gold-500/10 border border-gold-500/20 flex items-center justify-center mb-4">
      <Icon className="w-5 h-5 text-gold-300" />
    </div>
    <h3 className="text-lg font-bold text-white mb-2 leading-tight">{title}</h3>
    <p className="text-sm text-white/65 leading-relaxed">{body}</p>
    {footer && <div className="mt-4 pt-4 border-t border-white/[0.06] text-xs text-white/55">{footer}</div>}
  </div>
);

const Step = ({ number, title, body }) => (
  <div className="relative flex gap-5 pb-10 last:pb-0">
    <div className="flex flex-col items-center">
      <div className="w-11 h-11 rounded-full bg-gradient-to-b from-amber-300 to-gold-500 text-black font-black text-base flex items-center justify-center shadow-[0_8px_20px_-6px_rgba(212,175,55,0.4)]">
        {number}
      </div>
      <div className="w-px flex-1 bg-gradient-to-b from-gold-500/40 to-transparent mt-2 last:hidden" />
    </div>
    <div className="flex-1 pb-6">
      <h4 className="text-base font-bold text-white mb-1.5">{title}</h4>
      <p className="text-sm text-white/65 leading-relaxed">{body}</p>
    </div>
  </div>
);

const SectionLabel = ({ children }) => (
  <p className="text-[11px] uppercase tracking-[0.28em] text-gold-400 font-bold mb-4">
    {children}
  </p>
);

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function PartnersLanding() {
  return (
    <LanguageProvider>
      <PartnersLandingInner />
    </LanguageProvider>
  );
}

function PartnersLandingInner() {
  const { t } = useTranslation();
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'NoFee Partners Program',
    provider: { '@type': 'Organization', name: 'NoFee', url: 'https://nofee.restaurant' },
    description:
      '10% recurring commission for restaurant associations, chambers of commerce, and member organizations referring restaurants to NoFee.',
    areaServed: 'United States',
  };

  return (
    <div className="min-h-screen bg-black text-white antialiased">
      <SEO
        title="NoFee Partners — Recurring Revenue for Restaurant Organizations"
        description="Help your members eliminate delivery app fees while your organization earns 10% recurring commission. Built for restaurant associations, chambers of commerce, and food industry groups."
        path="/partners"
        jsonLd={jsonLd}
      />
      <Navbar forceSolid />

      {/* =====================================================================
          HERO
          ===================================================================== */}
      <header className="relative overflow-hidden pt-32 pb-24 lg:pt-44 lg:pb-32">
        {/* Background grain + spotlight */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(212,175,55,0.10),_transparent_60%)]" />
        <div className="absolute inset-0 opacity-[0.025] [background-image:url('data:image/svg+xml;utf8,<svg%20xmlns=%22http://www.w3.org/2000/svg%22%20width=%22120%22%20height=%22120%22%20viewBox=%220%200%20120%20120%22><filter%20id=%22n%22><feTurbulence%20baseFrequency=%220.9%22/></filter><rect%20width=%22100%25%22%20height=%22100%25%22%20filter=%22url(%23n)%22%20opacity=%220.5%22/></svg>')]" />

        <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-[1.4fr_1fr] gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gold-500/[0.08] border border-gold-500/25 text-[11.5px] uppercase tracking-[0.22em] text-gold-300 font-bold mb-6">
                <Sparkles className="w-3.5 h-3.5" />
                {t('partners.hero.kicker')}
              </div>
              <h1
                className="text-[40px] sm:text-5xl lg:text-[60px] leading-[1.04] font-black tracking-[-0.02em] text-white"
                data-testid="partners-hero-title"
              >
                {t('partners.hero.title_a')}{' '}
                <span className="bg-gradient-to-b from-emerald-200 to-emerald-400 bg-clip-text text-transparent">
                  {t('partners.hero.title_b')}
                </span>
                <br />
                {t('partners.hero.title_c')}{' '}
                <span className="bg-gradient-to-b from-amber-200 to-gold-400 bg-clip-text text-transparent">
                  {t('partners.hero.title_d')}
                </span>{' '}
                {t('partners.hero.title_e')}
              </h1>
              <p className="mt-7 text-lg lg:text-xl text-white/65 max-w-[680px] leading-relaxed">
                {t('partners.hero.subtitle')}
              </p>

              <div className="mt-10 flex flex-wrap gap-3">
                <Link
                  to="/partners/onboarding"
                  data-testid="partners-hero-cta-become"
                  className="inline-flex items-center gap-2 h-14 px-7 rounded-2xl bg-gradient-to-b from-amber-300 to-gold-500 hover:from-amber-200 hover:to-gold-400 text-black font-extrabold text-[15px] shadow-[0_12px_30px_-8px_rgba(212,175,55,0.55)] transition-all"
                >
                  {t('partners.hero.cta_become')}
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/partners/schedule-demo"
                  data-testid="partners-hero-cta-demo"
                  className="inline-flex items-center gap-2 h-14 px-7 rounded-2xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/15 hover:border-gold-500/40 text-white font-bold text-[15px] transition-all"
                >
                  {t('partners.hero.cta_demo')}
                </Link>
              </div>

              <p className="mt-6 text-xs text-white/40">
                {t('partners.hero.footnote')}
              </p>
            </div>

            {/* Hero side card — pitches the numbers visually */}
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-gold-500/[0.10] to-transparent blur-2xl rounded-3xl" />
              <div className="relative rounded-3xl border border-white/[0.08] bg-gradient-to-b from-zinc-900/80 to-black/60 backdrop-blur-md p-8 shadow-[0_30px_60px_-30px_rgba(0,0,0,0.8)]">
                <p className="text-[10.5px] uppercase tracking-[0.24em] text-gold-400 font-bold mb-4">
                  {t('partners.hero.card_label')}
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <StatCard icon={TrendingUp} label={t('partners.hero.stat_recurring')} value="10%" accent />
                  <StatCard icon={Wallet} label={t('partners.hero.stat_payouts')} value="Direct" />
                  <StatCard icon={ShieldCheck} label={t('partners.hero.stat_fees')} value="0%" />
                  <StatCard icon={CalendarClock} label={t('partners.hero.stat_trial')} value="45 days" />
                </div>
                <div className="mt-6 pt-6 border-t border-white/[0.06] flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gold-500/10 border border-gold-500/25 flex items-center justify-center">
                    <Lock className="w-4 h-4 text-gold-300" />
                  </div>
                  <p className="text-xs text-white/55 leading-snug">
                    {t('partners.hero.trust')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* =====================================================================
          NEW SUMMARY BLOCK — split benefits across restaurants vs organization
          to answer both questions in <5 seconds.
          ===================================================================== */}
      <section id="benefits" className="relative py-20 lg:py-28 border-t border-white/[0.04]">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <div className="max-w-[820px] mb-12">
            <SectionLabel>{t('partners.summary.label')}</SectionLabel>
            <h2 className="text-3xl lg:text-5xl font-black tracking-[-0.015em] text-white leading-[1.05]">
              {t('partners.summary.title_a')}<br />
              <span className="text-gold-300">{t('partners.summary.title_b')}</span>
            </h2>
          </div>

          <div
            className="grid md:grid-cols-2 gap-4 lg:gap-6 rounded-3xl overflow-hidden border border-white/[0.08]"
            data-testid="partners-summary-block"
          >
            {/* Left — Restaurants */}
            <div className="relative p-7 lg:p-10 bg-gradient-to-br from-zinc-950 to-black border-b md:border-b-0 md:border-r border-white/[0.06]">
              <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/[0.04] blur-3xl rounded-full pointer-events-none" />
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
                  <Store className="w-4.5 h-4.5 text-emerald-300" />
                </div>
                <p className="text-[10.5px] uppercase tracking-[0.24em] font-bold text-emerald-300">
                  {t('partners.summary.for_restaurants')}
                </p>
              </div>
              <ul className="space-y-4">
                {[
                  t('partners.mbenefits.b1_title'),
                  t('partners.mbenefits.b2_title'),
                  t('partners.mbenefits.b3_title'),
                  t('partners.mbenefits.b4_title'),
                  t('partners.mbenefits.fast_title'),
                ].map((b) => (
                  <li key={b} className="flex items-start gap-3 text-[15px] text-white/85" data-testid={`summary-restaurant-${b.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                    <CheckCircle2 className="w-5 h-5 text-emerald-300 flex-shrink-0 mt-0.5" />
                    <span className="font-semibold">{b}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Right — Organizations */}
            <div className="relative p-7 lg:p-10 bg-gradient-to-br from-zinc-900 to-black">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gold-500/[0.06] blur-3xl rounded-full pointer-events-none" />
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-gold-500/15 border border-gold-500/30 flex items-center justify-center">
                  <Building2 className="w-4.5 h-4.5 text-gold-300" />
                </div>
                <p className="text-[10.5px] uppercase tracking-[0.24em] font-bold text-gold-300">
                  {t('partners.summary.for_organizations')}
                </p>
              </div>
              <ul className="space-y-4">
                {[
                  t('partners.pbenefits.b1_title'),
                  t('partners.pbenefits.b2_title'),
                  t('partners.pbenefits.b5_title'),
                  t('partners.pbenefits.b6_title'),
                  t('partners.pbenefits.b7_title'),
                ].map((b) => (
                  <li key={b} className="flex items-start gap-3 text-[15px] text-white/85" data-testid={`summary-organization-${b.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                    <CheckCircle2 className="w-5 h-5 text-gold-300 flex-shrink-0 mt-0.5" />
                    <span className="font-semibold">{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* =====================================================================
          DEEP-DIVE LINKS — sends the visitor to the dedicated, NEVER-MIXED
          benefit pages. This is intentional per the IA refactor spec.
          ===================================================================== */}
      <section className="relative py-20 lg:py-28 bg-gradient-to-b from-transparent via-zinc-950/40 to-transparent">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <div className="max-w-[720px] mb-12">
            <SectionLabel>{t('partners.deep_links.label')}</SectionLabel>
            <h2 className="text-3xl lg:text-5xl font-black tracking-[-0.015em] text-white leading-[1.05]">
              {t('partners.deep_links.title_a')} <span className="text-gold-300">{t('partners.deep_links.title_b')}</span>
            </h2>
            <p className="mt-5 text-white/60 leading-relaxed">
              {t('partners.deep_links.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {/* For Restaurants */}
            <Link
              to="/partners/member-benefits"
              data-testid="partners-link-member-benefits"
              className="group relative rounded-3xl border border-emerald-500/25 bg-gradient-to-br from-emerald-500/[0.06] to-black/30 p-7 lg:p-9 transition-all hover:border-emerald-500/55 hover:from-emerald-500/[0.10]"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/[0.06] blur-3xl rounded-full pointer-events-none" />
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
                  <Store className="w-5 h-5 text-emerald-300" />
                </div>
                <p className="text-[10.5px] uppercase tracking-[0.24em] text-emerald-300 font-bold">
                  {t('partners.deep_links.member_kicker')}
                </p>
              </div>
              <h3 className="text-2xl lg:text-3xl font-black text-white leading-tight mb-3">
                {t('partners.deep_links.member_title')}
              </h3>
              <p className="text-sm text-white/65 leading-relaxed mb-5">
                {t('partners.deep_links.member_desc')}
              </p>
              <span className="inline-flex items-center gap-1.5 text-sm font-bold text-emerald-300 group-hover:gap-2.5 transition-all">
                {t('partners.deep_links.member_cta')} <ArrowRight className="w-4 h-4" />
              </span>
            </Link>

            {/* For Organizations */}
            <Link
              to="/partners/partner-benefits"
              data-testid="partners-link-partner-benefits"
              className="group relative rounded-3xl border border-gold-500/25 bg-gradient-to-br from-gold-500/[0.06] to-black/30 p-7 lg:p-9 transition-all hover:border-gold-500/55 hover:from-gold-500/[0.10]"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-gold-500/[0.06] blur-3xl rounded-full pointer-events-none" />
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 rounded-xl bg-gold-500/15 border border-gold-500/30 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-gold-300" />
                </div>
                <p className="text-[10.5px] uppercase tracking-[0.24em] text-gold-300 font-bold">
                  {t('partners.deep_links.partner_kicker')}
                </p>
              </div>
              <h3 className="text-2xl lg:text-3xl font-black text-white leading-tight mb-3">
                {t('partners.deep_links.partner_title')}
              </h3>
              <p className="text-sm text-white/65 leading-relaxed mb-5">
                {t('partners.deep_links.partner_desc')}
              </p>
              <span className="inline-flex items-center gap-1.5 text-sm font-bold text-gold-300 group-hover:gap-2.5 transition-all">
                {t('partners.deep_links.partner_cta')} <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* =====================================================================
          HOW IT WORKS
          ===================================================================== */}
      <section className="relative py-20 lg:py-28 bg-gradient-to-b from-transparent via-zinc-950/60 to-transparent border-y border-white/[0.04]">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
          <div className="max-w-[720px] mb-12">
            <SectionLabel>{t('partners.how.label')}</SectionLabel>
            <h2 className="text-3xl lg:text-5xl font-black tracking-tight text-white leading-tight">
              {t('partners.how.title_a')} <span className="text-gold-300">{t('partners.how.title_b')}</span>
            </h2>
          </div>

          <div className="grid lg:grid-cols-[1fr_1.3fr] gap-12">
            <div>
              <Step number={1} title={t('partners.how.step1_title')} body={t('partners.how.step1_body')} />
              <Step number={2} title={t('partners.how.step2_title')} body={t('partners.how.step2_body')} />
              <Step number={3} title={t('partners.how.step3_title')} body={t('partners.how.step3_body')} />
              <Step number={4} title={t('partners.how.step4_title')} body={t('partners.how.step4_body')} />
              <Step number={5} title={t('partners.how.step5_title')} body={t('partners.how.step5_body')} />
            </div>

            <aside className="rounded-3xl border border-gold-500/20 bg-gradient-to-br from-zinc-900 to-black p-8 lg:p-10 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.8)]">
              <p className="text-[11px] uppercase tracking-[0.24em] text-gold-400 font-bold mb-4">
                {t('partners.how.sample_label')}
              </p>
              <div className="rounded-xl bg-black/60 border border-white/[0.08] p-4 font-mono text-sm text-gold-200 break-all">
                nofee.restaurant/signup?partner=<span className="text-gold-400">ABC123</span>
              </div>

              <p className="mt-6 text-[11px] uppercase tracking-[0.24em] text-gold-400 font-bold mb-4">
                {t('partners.how.track_label')}
              </p>
              <ul className="space-y-3">
                {[
                  t('partners.how.track_1'),
                  t('partners.how.track_2'),
                  t('partners.how.track_3'),
                  t('partners.how.track_4'),
                  t('partners.how.track_5'),
                ].map((it) => (
                  <li key={it} className="flex items-start gap-3 text-sm text-white/75">
                    <CheckCircle2 className="w-4 h-4 text-gold-300 mt-0.5 flex-shrink-0" />
                    {it}
                  </li>
                ))}
              </ul>
            </aside>
          </div>
        </div>
      </section>

      {/* =====================================================================
          CTA / SECONDARY
          ===================================================================== */}
      <section className="relative py-20 lg:py-32">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
          <div className="relative rounded-3xl overflow-hidden border border-gold-500/25 bg-gradient-to-br from-zinc-900 via-black to-zinc-950 p-10 lg:p-16">
            <div className="absolute -top-32 -right-32 w-96 h-96 bg-gold-500/[0.08] blur-3xl rounded-full" />
            <div className="relative grid lg:grid-cols-[1.5fr_1fr] gap-10 items-center">
              <div>
                <SectionLabel>{t('partners.cta.label')}</SectionLabel>
                <h3 className="text-3xl lg:text-5xl font-black tracking-tight text-white leading-tight">
                  {t('partners.cta.title')}
                </h3>
                <p className="mt-5 text-white/65 text-base lg:text-lg leading-relaxed max-w-[560px]">
                  {t('partners.cta.subtitle')}
                </p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Link
                    to="/partners/onboarding"
                    data-testid="partners-bottom-cta-become"
                    className="inline-flex items-center gap-2 h-13 px-6 py-3.5 rounded-2xl bg-gradient-to-b from-amber-300 to-gold-500 hover:from-amber-200 hover:to-gold-400 text-black font-extrabold text-[15px] transition-all"
                  >
                    {t('partners.cta.become')}
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link
                    to="/partners/login"
                    data-testid="partners-bottom-cta-login"
                    className="inline-flex items-center gap-2 h-13 px-6 py-3.5 rounded-2xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/15 hover:border-gold-500/40 text-white font-bold text-[15px] transition-all"
                  >
                    {t('partners.cta.login')}
                  </Link>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-2xl border border-white/[0.08] p-5 bg-white/[0.02]">
                  <Building2 className="w-5 h-5 text-gold-300 mb-2" />
                  <p className="text-sm font-bold text-white">{t('partners.cta.cat_assoc')}</p>
                  <p className="text-xs text-white/55 mt-1">{t('partners.cta.cat_assoc_desc')}</p>
                </div>
                <div className="rounded-2xl border border-white/[0.08] p-5 bg-white/[0.02]">
                  <Users className="w-5 h-5 text-gold-300 mb-2" />
                  <p className="text-sm font-bold text-white">{t('partners.cta.cat_chamber')}</p>
                  <p className="text-xs text-white/55 mt-1">{t('partners.cta.cat_chamber_desc')}</p>
                </div>
                <div className="rounded-2xl border border-white/[0.08] p-5 bg-white/[0.02]">
                  <Sparkles className="w-5 h-5 text-gold-300 mb-2" />
                  <p className="text-sm font-bold text-white">{t('partners.cta.cat_influ')}</p>
                  <p className="text-xs text-white/55 mt-1">{t('partners.cta.cat_influ_desc')}</p>
                </div>
                <div className="rounded-2xl border border-white/[0.08] p-5 bg-white/[0.02]">
                  <Layers className="w-5 h-5 text-gold-300 mb-2" />
                  <p className="text-sm font-bold text-white">{t('partners.cta.cat_future')}</p>
                  <p className="text-xs text-white/55 mt-1">{t('partners.cta.cat_future_desc')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <FooterSection />
    </div>
  );
}
